import type { CommissionEntryView } from "@cx/contracts";
import { formatBRL, monthBounds, type CommissionEntryKind } from "@cx/domain";

export const ENTRY_KIND_LABEL: Record<CommissionEntryKind, string> = {
  commission: "Comissão",
  commission_reversal: "Estorno de comissão",
  credit: "Outro crédito",
  credit_reversal: "Estorno de crédito",
  debit: "Outro débito",
  debit_reversal: "Estorno de débito",
  payment: "Pagamento",
  payment_reversal: "Estorno de pagamento",
};

/** Histórico do lançamento no extrato: tipo + pedido (quando houver) + motivo. */
export function entryLabel(e: Pick<CommissionEntryView, "kind" | "orderNumber" | "notes">): string {
  const parts = [ENTRY_KIND_LABEL[e.kind]];
  if (e.orderNumber !== null) parts.push(`Pedido de venda nº ${e.orderNumber}`);
  if (e.notes) parts.push(e.notes);
  return parts.join(" · ");
}

/** Crédito, débito e pagamento ainda não estornados podem ser estornados (comissão sai pelo pedido). */
export const canReverseEntry = (e: Pick<CommissionEntryView, "kind" | "reversedAt">) =>
  !e.reversedAt && (e.kind === "credit" || e.kind === "debit" || e.kind === "payment");

/** Extrato do vendedor na API: id no caminho sempre codificado; período e cursor na query. */
export function statementPath(sellerId: string, query: Record<string, string>): string {
  const q = new URLSearchParams(query).toString();
  return `/commissions/sellers/${encodeURIComponent(sellerId)}${q ? `?${q}` : ""}`;
}

/** Estorno do lançamento: pagamento tem rota própria (cancela a conta a pagar). */
export const reversePath = (e: Pick<CommissionEntryView, "id" | "kind">): string =>
  `/commissions/${e.kind === "payment" ? "payments" : "entries"}/${encodeURIComponent(e.id)}/reverse`;

/** Valor com "—" quando o servidor não consegue somar com segurança (null). */
export const money = (c: number | null | undefined) => (c === null || c === undefined ? "—" : formatBRL(c));

/** Período de um mês "AAAA-MM" (input type=month); vazio ou inválido = null (o servidor usa o mês atual). */
export function monthPeriod(month: string): { from: string; to: string } | null {
  try {
    return month ? monthBounds(month) : null;
  } catch {
    return null;
  }
}

const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];
/** "outubro de 2026". */
export function monthName(month: string): string {
  const [y, m] = month.split("-");
  return `${MONTHS[Number(m) - 1] ?? "?"} de ${y}`;
}
